import { html } from 'lit';
import { sectionHeading } from '../components/sectionHeading.js';
import { teamHeroSection, teamMemberAnchor } from '../sections/teamHeroSection.js';
import { socialIcon } from '../components/socialIcons.js';
import { applicationPortal } from '../../shared/content/landingContent.js';
import type { LandingLocale } from '../../shared/content/landingContent.js';
import type {
  staticPageContentRepository,
  StaticPageContent as StaticPageData,
  StaticPageKey,
  StaticMissionStoryParagraphContent,
} from '../../shared/content/staticPageContent.js';
import { staticPageUiCopy } from '../../shared/content/staticPageLocalization.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

const contactActionHref = (index: number, contact: NonNullable<ReturnType<typeof staticPageContentRepository.getPage>['contact']>) => {
  if (index === 0) return `mailto:${contact.email}`;
  if (index === 1) return `tel:${contact.phone.replaceAll(' ', '')}`;
  return 'https://www.openstreetmap.org/?mlat=51.554&mlon=7.312#map=15/51.554/7.312';
};

export const renderContactAction = (
  pageKey: StaticPageKey,
  index: number,
  title: string,
  iconClass: string,
  text: string,
  contact: NonNullable<ReturnType<typeof staticPageContentRepository.getPage>['contact']>,
  editor?: LandingEditorRenderOptions,
) => {
  const href = contactActionHref(index, contact);
  const cardId = `static.${pageKey}.card.${index}`;

  return html`
    <a
      class=${editorNodeClass(editor, cardId, 'vp-contact-action')}
      style=${editorNodeStyle(editor, cardId)}
      href=${href}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, cardId, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, cardId, event)}
    >
      <span
        class=${editorNodeClass(editor, `${cardId}.icon`, iconClass)}
        style=${editorNodeStyle(editor, `${cardId}.icon`)}
        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${cardId}.icon`, event)}
        @click=${(event: MouseEvent) => editorNodeClick(editor, `${cardId}.icon`, event)}
      ></span>
      <strong
        class=${editorNodeClass(editor, `${cardId}.title`)}
        style=${editorNodeStyle(editor, `${cardId}.title`)}
        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${cardId}.title`, event)}
        @click=${(event: MouseEvent) => editorNodeClick(editor, `${cardId}.title`, event)}
      >
        ${title}
      </strong>
      <p
        class=${editorNodeClass(editor, `${cardId}.text`)}
        style=${editorNodeStyle(editor, `${cardId}.text`)}
        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${cardId}.text`, event)}
        @click=${(event: MouseEvent) => editorNodeClick(editor, `${cardId}.text`, event)}
      >
        ${text}
      </p>
    </a>
  `;
};

export const renderMissionStoryParagraph = (paragraph: StaticMissionStoryParagraphContent, elementId: string, editor?: LandingEditorRenderOptions) => {
  const defaultText = paragraph.parts.map(part => part.text).join('');
  const editedText = paragraph.text ?? defaultText;
  const shouldRenderDefaultHighlights = editedText === defaultText;

  return html`
    <p
      class=${editorNodeClass(editor, elementId)}
      style=${editorNodeStyle(editor, elementId)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, elementId, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, elementId, event)}
    >
      ${shouldRenderDefaultHighlights ? paragraph.parts.map(part => (part.strong ? html`<strong>${part.text}</strong>` : part.text)) : editedText}
    </p>
  `;
};

export const renderMissionStory = (page: StaticPageData, prefix: string, sectionStyle: string, editor?: LandingEditorRenderOptions) => {
  const story = page.missionStory;
  if (!story) return '';

  return html`
    <section
      class=${editorNodeClass(editor, `${prefix}.missionStory`, 'vp-section vp-mission-story-section')}
      style=${sectionStyle}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.missionStory`, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.missionStory`, event)}
    >
      <div class="vp-shell">
        <div class="vp-mission-story-intro">
          <p
            class=${editorNodeClass(editor, `${prefix}.missionStory.eyebrow`, 'vp-eyebrow')}
            style=${editorNodeStyle(editor, `${prefix}.missionStory.eyebrow`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.missionStory.eyebrow`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.missionStory.eyebrow`, event)}
          >
            ${story.eyebrow}
          </p>
          <h2
            class=${editorNodeClass(editor, `${prefix}.missionStory.title`)}
            style=${editorNodeStyle(editor, `${prefix}.missionStory.title`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.missionStory.title`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.missionStory.title`, event)}
          >
            ${story.title}
          </h2>
          <p
            class=${editorNodeClass(editor, `${prefix}.missionStory.slogan`, 'vp-mission-story-slogan')}
            style=${editorNodeStyle(editor, `${prefix}.missionStory.slogan`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.missionStory.slogan`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.missionStory.slogan`, event)}
          >
            ${story.slogan}
          </p>
          <p
            class=${editorNodeClass(editor, `${prefix}.missionStory.intro`, 'vp-mission-story-lead')}
            style=${editorNodeStyle(editor, `${prefix}.missionStory.intro`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.missionStory.intro`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.missionStory.intro`, event)}
          >
            ${story.intro}
          </p>
        </div>

        <div class="vp-mission-story-blocks">
          ${story.blocks.map((block, blockIndex) => {
            const blockId = `${prefix}.missionStory.block.${blockIndex}`;
            return html`
              <article
                class=${editorNodeClass(editor, blockId, `vp-mission-story-block ${blockIndex % 2 ? 'vp-mission-story-block-reverse' : ''}`)}
                style=${editorNodeStyle(editor, blockId)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, blockId, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, blockId, event)}
              >
                <div class="vp-mission-story-copy">
                  <h3
                    class=${editorNodeClass(editor, `${blockId}.title`, 'vp-mission-story-block-title')}
                    style=${editorNodeStyle(editor, `${blockId}.title`)}
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${blockId}.title`, event)}
                    @click=${(event: MouseEvent) => editorNodeClick(editor, `${blockId}.title`, event)}
                  >
                    ${block.title}
                  </h3>
                  <div class="vp-mission-story-paragraphs">
                    ${block.paragraphs.map((paragraph, paragraphIndex) => html` <div class="vp-mission-story-note">${renderMissionStoryParagraph(paragraph, `${blockId}.paragraph.${paragraphIndex}`, editor)}</div> `)}
                  </div>
                </div>
              </article>
            `;
          })}
        </div>

        <aside class="vp-mission-story-closing">
          <h3
            class=${editorNodeClass(editor, `${prefix}.missionStory.closing.title`)}
            style=${editorNodeStyle(editor, `${prefix}.missionStory.closing.title`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.missionStory.closing.title`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.missionStory.closing.title`, event)}
          >
            ${story.closing.title}
          </h3>
          <div>${story.closing.paragraphs.map((paragraph, paragraphIndex) => html` ${renderMissionStoryParagraph(paragraph, `${prefix}.missionStory.closing.paragraph.${paragraphIndex}`, editor)} `)}</div>
        </aside>
      </div>
    </section>
  `;
};

export const renderTeamHero = (page: StaticPageData, prefix: string, sectionStyle: string, currentLocale: LandingLocale, editor?: LandingEditorRenderOptions) =>
  teamHeroSection({ page, prefix, sectionStyle, currentLocale, editor });

const teamPageGroupMembers = [
  ['Enes Vural', 'Serife Vural-Banik'],
  ['Wilhelm Josten', 'Murat Vuval'],
  ['Dr. Kirstin Wingler', 'Ergün Yalcin'],
];

const toggleTeamCard = (event: MouseEvent, editor?: LandingEditorRenderOptions, elementId?: string) => {
  if (editor?.mode === 'editor') {
    if (elementId) editorNodeClick(editor, elementId, event);
    return;
  }

  if ((event.target as HTMLElement).closest('a')) return;
  const card = event.currentTarget as HTMLElement;
  const willFlip = !card.classList.contains('is-flipped');
  card
    .closest('.vp-team-grid')
    ?.querySelectorAll<HTMLElement>('.vp-team-member.is-flipped')
    .forEach(other => {
      if (other === card) return;
      other.classList.remove('is-flipped');
      other.setAttribute('aria-pressed', 'false');
    });
  card.classList.toggle('is-flipped', willFlip);
  card.setAttribute('aria-pressed', String(willFlip));
};

const handleTeamCardKeydown = (event: KeyboardEvent, editor?: LandingEditorRenderOptions, elementId?: string) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  toggleTeamCard(event as unknown as MouseEvent, editor, elementId);
};

export const renderTeamMemberCard = (member: NonNullable<StaticPageData['team']>[number], index: number, prefix: string, currentLocale: LandingLocale, editor?: LandingEditorRenderOptions) => {
  const memberId = `${prefix}.team.member.${index}`;
  const hasBackContent = Boolean(member.story || member.motivation);

  return html`
    <article
      id=${teamMemberAnchor(index)}
      class=${editorNodeClass(editor, memberId, `vp-team-member reveal-card${hasBackContent ? ' vp-team-member-flippable' : ''}`)}
      style=${editorNodeStyle(editor, memberId)}
      tabindex="0"
      role=${hasBackContent ? 'button' : 'article'}
      aria-pressed=${hasBackContent ? 'false' : undefined}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, memberId, event)}
      @click=${(event: MouseEvent) => (hasBackContent ? toggleTeamCard(event, editor, memberId) : editorNodeClick(editor, memberId, event))}
      @keydown=${(event: KeyboardEvent) => (hasBackContent ? handleTeamCardKeydown(event, editor, memberId) : undefined)}
    >
      <div class="vp-team-member-inner">
        <div class="vp-team-member-face vp-team-member-face-front">
          <div class="vp-team-member-media">
            <img
              class=${editorNodeClass(editor, `${memberId}.image`)}
              style=${editorNodeStyle(editor, `${memberId}.image`)}
              src=${member.image}
              alt=${member.name}
              loading="lazy"
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.image`, event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.image`, event)}
            />
            <span class="vp-team-member-role-pill">${member.role}</span>
            ${hasBackContent
              ? html`
                  <span class="vp-team-flip-cue" aria-hidden="true">
                    <span class="icon-[material-symbols--arrow-forward-rounded]"></span>
                  </span>
                `
              : ''}
          </div>
          <div class="vp-team-member-body">
            <h3
              class=${editorNodeClass(editor, `${memberId}.name`)}
              style=${editorNodeStyle(editor, `${memberId}.name`)}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.name`, event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.name`, event)}
            >
              ${member.name}
            </h3>
            <span
              class=${editorNodeClass(editor, `${memberId}.role`, 'vp-team-member-role')}
              style=${editorNodeStyle(editor, `${memberId}.role`)}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.role`, event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.role`, event)}
            >
              ${member.role}
            </span>
            <p
              class=${editorNodeClass(editor, `${memberId}.text`)}
              style=${editorNodeStyle(editor, `${memberId}.text`)}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.text`, event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.text`, event)}
            >
              ${member.text}
            </p>
            ${member.linkedinUrl || editor
              ? html`
                  <a
                    class=${editorNodeClass(editor, `${memberId}.linkedinUrl`, 'vp-team-linkedin')}
                    style=${editorNodeStyle(editor, `${memberId}.linkedinUrl`)}
                    href=${member.linkedinUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label=${staticPageUiCopy(currentLocale).linkedInProfile(member.name)}
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.linkedinUrl`, event)}
                    @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.linkedinUrl`, event)}
                  >
                    ${socialIcon(member.linkedinUrl || 'https://www.linkedin.com/')}
                    <span>LinkedIn</span>
                  </a>
                `
              : ''}
          </div>
        </div>
        ${hasBackContent || editor
          ? html`
              <div class="vp-team-member-face vp-team-member-face-back">
                <div class="vp-team-member-back-scroll">
                  <span class="vp-team-member-story-label">${staticPageUiCopy(currentLocale).story}</span>
                  <p
                    class=${editorNodeClass(editor, `${memberId}.story`)}
                    style=${editorNodeStyle(editor, `${memberId}.story`)}
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.story`, event)}
                    @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.story`, event)}
                  >
                    ${member.story || member.text}
                  </p>
                  <span class="vp-team-member-story-label">${staticPageUiCopy(currentLocale).motivation}</span>
                  <p
                    class=${editorNodeClass(editor, `${memberId}.motivation`)}
                    style=${editorNodeStyle(editor, `${memberId}.motivation`)}
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${memberId}.motivation`, event)}
                    @click=${(event: MouseEvent) => editorNodeClick(editor, `${memberId}.motivation`, event)}
                  >
                    ${member.motivation || ''}
                  </p>
                </div>
              </div>
            `
          : ''}
      </div>
    </article>
  `;
};

export const renderTeamGroups = (page: StaticPageData, prefix: string, currentLocale: LandingLocale, editor?: LandingEditorRenderOptions) => {
  const members = page.team ?? [];
  const groupTitles = staticPageUiCopy(currentLocale).teamGroups;
  return html`
    <div class="vp-team-groups">
      ${teamPageGroupMembers.map((names, groupIndex) => {
        const groupMembers = names
          .map(name => {
            const index = members.findIndex(member => member.name === name);
            return index >= 0 ? { member: members[index], index } : null;
          })
          .filter((entry): entry is { member: NonNullable<StaticPageData['team']>[number]; index: number } => Boolean(entry));
        if (!groupMembers.length) return '';
        return html`
          <section class="vp-team-group" aria-label=${groupTitles[groupIndex]}>
            <div class="vp-team-group-heading">
              <h3>${groupTitles[groupIndex]}</h3>
            </div>
            <div class="vp-team-grid vp-team-grid-grouped">${groupMembers.map(({ member, index }) => renderTeamMemberCard(member, index, prefix, currentLocale, editor))}</div>
          </section>
        `;
      })}
    </div>
  `;
};

export const renderGlobusHero = (page: StaticPageData, prefix: string, sectionStyle: string, editor?: LandingEditorRenderOptions) => {
  const heroImages: Array<{ image: string; alt: string; kind?: 'photo' | 'logo' }> = page.heroGallery?.length
    ? page.heroGallery.map(item => ({ image: item.image, alt: item.imageAlt, kind: item.kind }))
    : [...(page.sections?.flatMap(section => section.cards?.map(card => ({ image: card.image, alt: card.imageAlt ?? card.title })) ?? []) ?? []), { image: page.heroImage, alt: page.title }].filter(
        (item): item is { image: string; alt: string } => Boolean(item.image),
      );
  const heroId = `${prefix}.hero`;

  return html`
    <section
      class=${editorNodeClass(editor, heroId, 'vp-static-hero vp-team-hero vp-globus-hero')}
      style=${sectionStyle}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, heroId, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, heroId, event)}
    >
      <div class="vp-team-hero-word vp-globus-hero-word" aria-hidden="true">Globus</div>
      <div class="vp-team-hero-lines" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <div class="vp-shell vp-team-hero-layout vp-globus-hero-layout">
        <div class="vp-globus-hero-copy">
          <p
            class=${editorNodeClass(editor, `${heroId}.eyebrow`, 'vp-eyebrow')}
            style=${editorNodeStyle(editor, `${heroId}.eyebrow`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${heroId}.eyebrow`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${heroId}.eyebrow`, event)}
          >
            ${page.eyebrow}
          </p>
          <h1
            class=${editorNodeClass(editor, `${heroId}.title`)}
            style=${editorNodeStyle(editor, `${heroId}.title`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${heroId}.title`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${heroId}.title`, event)}
          >
            ${page.title}
          </h1>
          <p
            class=${editorNodeClass(editor, `${heroId}.intro`)}
            style=${editorNodeStyle(editor, `${heroId}.intro`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${heroId}.intro`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${heroId}.intro`, event)}
          >
            ${page.intro}
          </p>
        </div>
        <div class="vp-team-hero-stage vp-globus-hero-stage ${heroImages.length === 1 ? 'vp-globus-hero-stage--single' : heroImages.length === 3 ? 'vp-globus-hero-stage--three' : ''}" aria-label="Globus">
          ${heroImages.slice(0, 4).map((item, index) => {
            const portraitId = `${heroId}.portrait.${index}`;
            return html`
              <figure
                class=${editorNodeClass(editor, portraitId, `vp-team-hero-portrait vp-globus-hero-portrait vp-team-hero-portrait-${index} ${item.kind === 'logo' ? 'vp-globus-hero-portrait--logo' : ''}`)}
                style=${editorNodeStyle(editor, portraitId)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, portraitId, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, portraitId, event)}
              >
                <img
                  class=${editorNodeClass(editor, `${portraitId}.image`)}
                  style=${editorNodeStyle(editor, `${portraitId}.image`)}
                  src=${item.image}
                  alt=${item.alt}
                  decoding="async"
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${portraitId}.image`, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, `${portraitId}.image`, event)}
                />
              </figure>
            `;
          })}
        </div>
      </div>
    </section>
  `;
};

export const renderContactHero = (page: StaticPageData, prefix: string, currentLocale: LandingLocale, sectionStyle: string, editor?: LandingEditorRenderOptions) => {
  const heroId = `${prefix}.hero`;
  const stageLabel = currentLocale === 'tr' ? 'Size doğrudan ulaşabiliriz' : currentLocale === 'en' ? 'Reach us directly' : 'Direkt erreichbar';
  const responseLabel = currentLocale === 'tr' ? 'Kişisel, açık ve bağlayıcı' : currentLocale === 'en' ? 'Personal, clear and reliable' : 'Persönlich, klar und verbindlich';

  return html`
    <section
      class=${editorNodeClass(editor, heroId, 'vp-static-hero vp-team-hero vp-contact-hero')}
      style=${sectionStyle}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, heroId, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, heroId, event)}
    >
      <div class="vp-team-hero-word vp-contact-hero-word" aria-hidden="true">${currentLocale === 'tr' ? 'İletişim' : currentLocale === 'en' ? 'Contact' : 'Kontakt'}</div>
      <div class="vp-team-hero-lines" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
      </div>
      <div class="vp-shell vp-team-hero-layout vp-contact-hero-layout">
        <div class="vp-contact-hero-copy">
          <p
            class=${editorNodeClass(editor, `${heroId}.eyebrow`, 'vp-eyebrow')}
            style=${editorNodeStyle(editor, `${heroId}.eyebrow`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${heroId}.eyebrow`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${heroId}.eyebrow`, event)}
          >
            ${page.eyebrow}
          </p>
          <h1
            class=${editorNodeClass(editor, `${heroId}.title`)}
            style=${editorNodeStyle(editor, `${heroId}.title`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${heroId}.title`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${heroId}.title`, event)}
          >
            ${page.title}
          </h1>
          <p
            class=${editorNodeClass(editor, `${heroId}.intro`)}
            style=${editorNodeStyle(editor, `${heroId}.intro`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${heroId}.intro`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${heroId}.intro`, event)}
          >
            ${page.intro}
          </p>
        </div>
        <div class="vp-contact-hero-stage" aria-label=${stageLabel}>
          <div class="vp-contact-hero-mark" aria-hidden="true">
            <span class="icon-[material-symbols--forward-to-inbox-rounded]"></span>
          </div>
          <div class="vp-contact-hero-stage-copy">
            <span>${stageLabel}</span>
            <a href="mailto:${page.contact?.email ?? ''}">${page.contact?.email}</a>
            <a href="tel:${page.contact?.phone.replaceAll(' ', '') ?? ''}">${page.contact?.phone}</a>
          </div>
          <div class="vp-contact-hero-promise">
            <span class="icon-[material-symbols--verified-rounded]" aria-hidden="true"></span>
            ${responseLabel}
          </div>
        </div>
      </div>
    </section>
  `;
};

export const renderImageCardSection = (
  pageKey: StaticPageKey,
  prefix: string,
  section: NonNullable<StaticPageData['sections']>[number],
  sectionIndex: number,
  sectionStyle: string,
  currentLocale: LandingLocale,
  editor?: LandingEditorRenderOptions,
) => html`
  <section
    class=${editorNodeClass(editor, `${prefix}.section.${sectionIndex}`, 'vp-section vp-globus-jobs-section')}
    style=${sectionStyle}
    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${prefix}.section.${sectionIndex}`, event)}
    @click=${(event: MouseEvent) => editorNodeClick(editor, `${prefix}.section.${sectionIndex}`, event)}
  >
    <div class="vp-shell">
      ${sectionHeading({
        headingTag: pageKey === 'einzelhandel' ? 'h2' : undefined,
        eyebrow: section.eyebrow,
        title: section.title,
        highlight: section.titleHighlight,
        text: section.text,
        align: 'center',
        editor,
        elementPrefix: `static.${pageKey}.section.${sectionIndex}`,
      })}
      <div class="vp-globus-jobs-grid">
        ${section.cards?.map((card, cardIndex) => {
          const cardId = `${prefix}.section.${sectionIndex}.card.${cardIndex}`;
          const applicationHref =
            cardIndex === 1
              ? applicationPortal.metzger
              : applicationPortal.einzelhandelFocus(cardIndex === 0 ? 'baeckerei' : 'frischetheke');
          return html`
            <article
              class=${editorNodeClass(editor, cardId, 'vp-globus-job-card reveal-card')}
              style=${editorNodeStyle(editor, cardId)}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, cardId, event)}
              @click=${(event: MouseEvent) => editorNodeClick(editor, cardId, event)}
            >
              ${card.image
                ? html`<img
                    class=${editorNodeClass(editor, `${cardId}.image`)}
                    style=${editorNodeStyle(editor, `${cardId}.image`)}
                    src=${card.image}
                    alt=${card.imageAlt ?? card.title}
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${cardId}.image`, event)}
                    @click=${(event: MouseEvent) => editorNodeClick(editor, `${cardId}.image`, event)}
                  />`
                : ''}
              <div class="vp-globus-job-card-copy">
                <span
                  class=${editorNodeClass(editor, `${cardId}.icon`, card.iconClass)}
                  style=${editorNodeStyle(editor, `${cardId}.icon`)}
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${cardId}.icon`, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, `${cardId}.icon`, event)}
                ></span>
                <h3
                  class=${editorNodeClass(editor, `${cardId}.title`)}
                  style=${editorNodeStyle(editor, `${cardId}.title`)}
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${cardId}.title`, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, `${cardId}.title`, event)}
                >
                  ${card.title}
                </h3>
                <p
                  class=${editorNodeClass(editor, `${cardId}.text`)}
                  style=${editorNodeStyle(editor, `${cardId}.text`)}
                  @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${cardId}.text`, event)}
                  @click=${(event: MouseEvent) => editorNodeClick(editor, `${cardId}.text`, event)}
                >
                  ${card.text}
                </p>
                <a
                  class="vp-globus-job-cta"
                  href=${applicationHref}
                  @click=${(event: MouseEvent) => {
                    event.stopPropagation();
                  }}
                >
                  ${staticPageUiCopy(currentLocale).applicationForm}
                </a>
              </div>
            </article>
          `;
        })}
      </div>
    </div>
  </section>
`;
